import React, { useState } from "react";
import copy from "copy-to-clipboard";
import { TableRow, TableCell } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pencil, Copy, Check } from "lucide-react";
import LinkToggle from "./LinkToggle";

/**
 * Secondary Link Row Component
 * Modernized with shadcn components - Row item for secondary links table
 */
function SecondaryLink_row(props) {
    const { 
        userName, 
        createdOn, 
        linkType, 
        linkSuffix, 
        groupNumberId, 
        objectUID,
        objectUid,
        groupNumber, 
        isActive, 
        signupCount,
        toggle_add_edit_popup
    } = props;

    const [copied, setCopied] = useState(false);
    const [isActiveState, setIsActiveState] = useState(isActive);

    const handleCopy = () => {
        copy(window.location.origin + "/signup/" + linkSuffix);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleEdit = () => {
        const event = {
            currentTarget: {
                getAttribute: (attr) => {
                    const attrs = {
                        assigneduser: userName,
                        groupnumberid: groupNumberId,
                        linktype: linkType,
                        linksuffix: linkSuffix,
                        objectuid: objectUID || objectUid,
                        groupnumber: groupNumber
                    };
                    return attrs[attr];
                }
            },
            stopPropagation: () => {}
        };
        toggle_add_edit_popup(event);
    };

    const handleToggle = () => {
        setIsActiveState(!isActiveState);
    };

    if (!userName) return null;

    return (
        <TableRow className="hover:bg-muted/30">
            <TableCell className="font-medium">{userName}</TableCell>
            <TableCell>{groupNumber}</TableCell>
            <TableCell>{createdOn}</TableCell>
            <TableCell>
                <Badge variant="outline" className="text-xs">
                    {linkType}
                </Badge>
            </TableCell>
            <TableCell className="font-mono text-sm">{linkSuffix}</TableCell>
            <TableCell className="text-center">{signupCount || 0}</TableCell>
            <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                    <LinkToggle 
                        assigneduser={userName}
                        groupnumberid={groupNumberId}
                        linktype={linkType}
                        linksuffix={linkSuffix}
                        objectuid={objectUID || objectUid}
                        groupnumber={groupNumber}
                        isactive={isActive}
                        deactivated={handleToggle}
                    />
                    {isActiveState && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleEdit}
                                        className="h-8 w-8 p-0"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit User</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleCopy}
                                        className="h-8 w-8 p-0"
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{copied ? 'Copied' : 'Copy Signup Link'}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}

// ─── Mobile card ─────────────────────────────────────────────────────────────
export function SecondaryLinkCard(props) {
    const {
        userName,
        createdOn,
        linkType,
        linkSuffix,
        groupNumberId,
        objectUID,
        objectUid,
        groupNumber,
        isActive,
        signupCount,
        toggle_add_edit_popup,
    } = props;

    const [copied, setCopied] = useState(false);
    const [isActiveState, setIsActiveState] = useState(isActive);

    if (!userName) return null;

    const handleCopy = () => {
        copy(window.location.origin + "/signup/" + linkSuffix);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleEdit = () => {
        const event = {
            currentTarget: {
                getAttribute: (attr) => ({
                    assigneduser: userName,
                    groupnumberid: groupNumberId,
                    linktype: linkType,
                    linksuffix: linkSuffix,
                    objectuid: objectUID || objectUid,
                    groupnumber: groupNumber,
                }[attr]),
            },
            stopPropagation: () => {},
        };
        toggle_add_edit_popup(event);
    };

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                {/* Header: user + link-type badge + toggle */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="font-semibold text-sm text-primary truncate">{userName}</p>
                        <Badge variant="outline" className="text-xs mt-1">{linkType}</Badge>
                    </div>
                    <LinkToggle
                        objectuid={objectUID || objectUid}
                        linksuffix={linkSuffix}
                        isactive={isActive}
                        deactivated={() => setIsActiveState(!isActiveState)}
                    />
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t pt-3">
                    <div>
                        <p className="text-xs text-muted-foreground">Group Number</p>
                        <p className="font-medium mt-0.5">{groupNumber || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Created On</p>
                        <p className="font-medium mt-0.5">{createdOn || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Link Suffix</p>
                        <p className="font-mono font-medium mt-0.5 text-sm">{linkSuffix || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Signups</p>
                        <p className="font-medium mt-0.5">{signupCount || 0}</p>
                    </div>
                </div>

                {/* Actions */}
                {isActiveState && (
                    <div className="flex gap-2 border-t pt-3">
                        <Button variant="outline" size="sm" className="gap-2" onClick={handleEdit}>
                            <Pencil className="h-4 w-4" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" className="gap-2" onClick={handleCopy}>
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {copied ? 'Copied!' : 'Copy Link'}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default SecondaryLink_row;
